import React, { useCallback, useState } from "react";

import { DownMinimalArrow } from "icons";

import usersApi from "apis/users";
import {
  SubHeader,
  Typography,
  Button,
  Checkbox,
  DropdownOverlay,
  SearchBar,
} from "clearsense-ui/components";
import ContentBox from "components/Common/ContentBox";
import Filter from "components/Common/FilterHeader/Filter";
import QuickFilter from "components/Common/QuickFilter";
import SummaryLineChart from "components/Common/SummaryLineChart";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import {
  QUICK_FILTER_DATA,
  STEWARD_TABLE_COLUMN,
  SUMMARY_GRAPH_DATA,
} from "./constants";
import StewardsTable from "./Table";

import {
  FILTER_CONDITIONS,
  FILTER_OPERATIONS,
} from "../../constants/stewardFilter";

const PeopleHome = () => {
  const [stewardData, setStewardData] = useState([]);
  const [filterConditions, setFilterConditions] = useState([]);
  const [stewardPagination, setStewardPagination] = useState();
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [quickFilterData, setQuickFilterData] = useState([]);
  const [filter, setFilter] = useState({
    filterBy: "",
    filterValue: "",
  });

  const fetchUsers = useCallback(async () => {
    try {
      setIsTableLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const { data } = await usersApi.fetch(
        currentPage,
        pageSize,
        search.toLowerCase(),
        filter.filterBy,
        filter.filterValue
      );
      setStewardData(data?.users);
      setStewardPagination(data?.users_pagination);
      setQuickFilterData(
        QUICK_FILTER_DATA(
          setFilter,
          data?.data_steward_count,
          data?.application_steward_count,
          data?.information_governor_count
        )
      );
    } catch (error) {
      logger.error(error);
    } finally {
      setIsTableLoading(false);
    }
  }, [filter]);

  const {
    search,
    setSearch,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
  } = useSearchAndPaginate(fetchUsers);

  const { columns, toggleVisibility } = useColumnHeaderSelector(
    STEWARD_TABLE_COLUMN()
  );

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <SubHeader
        breadcrumbs={[
          { label: "People", link: "#" },
          { label: "People", link: "#" },
        ]}
        className="mt-8 mb-2"
        actionBlock={
          <Button
            label="New Person"
            style="primary"
            data-cy="new-person-button"
          />
        }
      />
      <ContentBox className="flex flex-col justify-center space-y-4 mt-4 items-center bg-white dark:bg-[#2f3941] w-full dark:text-white">
        <Typography type="semibold" style="h3" className="justify-center flex">
          Summary
        </Typography>
        <div className="flex space-x-4 w-full">
          <QuickFilter className="w-1/2" quickFilterData={quickFilterData} />
          <SummaryLineChart
            title="Role Growth"
            data={SUMMARY_GRAPH_DATA}
            className="w-1/2"
          />
        </div>
      </ContentBox>
      <div className="flex justify-between gap-2 my-4">
        <div className="flex items-center w-2/6 gap-3">
          <Typography style="large" type="bold">
            {stewardPagination?.total_pagination} Stewards
          </Typography>
          <Filter
            title="People"
            filterConditions={filterConditions}
            setFilterConditions={setFilterConditions}
            columnOptions={STEWARD_TABLE_COLUMN().map(column => ({
              label: column.title,
              value: column.title,
            }))}
            conditionOptions={FILTER_CONDITIONS}
            filterOperations={FILTER_OPERATIONS}
          />
        </div>
        <div className="flex items-center gap-2 mt-0">
          <SearchBar
            className="w-72 ml-1"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-cy="people-search"
          />
          <div
            className="flex gap-4"
            data-cy="people-table-edit-column-button-div"
          >
            <DropdownOverlay
              buttonLabel="Edit Columns"
              buttonStyle="secondary"
              buttonProps={{ icon: DownMinimalArrow, iconPosition: "right" }}
            >
              <div
                className="p-4 pb-0"
                data-cy="people-table-enabled-columns-div"
              >
                <Typography
                  style="h6"
                  className="mb-2 uppercase text-grey-medium-shade"
                >
                  enabled
                </Typography>
                {columns?.selected?.map(column => (
                  <Checkbox
                    key={column.key}
                    label={column.title}
                    id={column.key}
                    className="mb-5"
                    onClick={e =>
                      columns.selected.length > 1 &&
                      toggleVisibility(e.target.id)
                    }
                    disabled={columns.selected.length === 1}
                    checked
                    readOnly
                  />
                ))}
              </div>
              <div className="px-4" data-cy="people-table-disabled-columns-div">
                <Typography
                  style="h6"
                  className="mb-2 uppercase text-grey-medium-shade"
                >
                  disabled
                </Typography>
                {columns.unSelected.map(column => (
                  <Checkbox
                    key={column.key}
                    label={column.title}
                    id={column.key}
                    className="mb-5"
                    onClick={e => toggleVisibility(e.target.id)}
                    readOnly
                  />
                ))}
              </div>
            </DropdownOverlay>
          </div>
        </div>
      </div>
      <StewardsTable
        columns={columns}
        stewardData={stewardData}
        stewardPagination={stewardPagination}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isLoading={isTableLoading}
      />
    </div>
  );
};

export default PeopleHome;
